
import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  checkQRStatus,
  redeemPromotionalQr,
  sendOTP,
  getConsumerDetails,
} from "../../redux/actions/consumerActions";
import { LoadScriptNext, Autocomplete } from "@react-google-maps/api";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";


const libraries = ["places"];

const Field = ({ label, children }) => (
  <div className="mb-4">
    <label className="block text-xs font-semibold text-amber-700 uppercase tracking-wider mb-1.5">
      {label}
    </label>
    {children}
  </div>
);

const inputClass =
  "w-full border border-gray-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-100 outline-none rounded-xl px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 transition";

const PromotionalForm = () => {
  const { qr_id } = useParams();
  const dispatch = useDispatch();

  const [showSplash, setShowSplash] = useState(false);
  const [showText, setShowText] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState(["", "", "", ""]); // 4-digit OTP
  const [otpError, setOtpError] = useState("");
  const otpRefs = useRef([]);

  const [autoFilled, setAutoFilled] = useState(false);
  const [skipOtp, setSkipOtp] = useState(false);
  // ✅ alreadyRegistered now only controls showing a Login banner, NOT hiding the form
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [consumerLoginUrl, setConsumerLoginUrl] = useState("");
  const [mobileError, setMobileError] = useState("");
  const mobileDebounceRef = useRef(null);
  const autocompleteRef = useRef(null);

  // ── Selectors ──────────────────────────────────────────────────────────────
  const loading = useSelector((state) => state.qrStatus.loading);
  const error = useSelector((state) => state.qrStatus.error);
  const statusInfo = useSelector((state) => state.qrStatus.statusInfo);
  const otpLoading = useSelector((state) => state.otp?.loading);
  const otpApiError = useSelector((state) => state.otp?.otpError);

  // ✅ Uses promotionalRedeem slice
  const redeemSuccess = useSelector((state) => state.promotionalRedeem?.success);
  const redeemData = useSelector((state) => state.promotionalRedeem?.redeemData);
  const redeemError = useSelector((state) => state.promotionalRedeem?.error);
  const verifyLoading = useSelector((state) => state.promotionalRedeem?.loading);
  const [showPassword, setShowPassword] = useState(false);

  const { loading: customerLoading } = useSelector(
    (state) => state.customerDetails
  );

  // ── Form type detection ────────────────────────────────────────────────────
  const isPromotionalForm = statusInfo?.order_type === "promotional";

  const isCashbackForm =
    statusInfo?.order_type === "cashback" &&
    statusInfo?.fields?.field_1 === "Name" &&
    statusInfo?.fields?.field_2 === "Mobile" &&
    statusInfo?.fields?.field_3 === "UPI ID";

  const showForm = isPromotionalForm || isCashbackForm;

  // ── Form state ─────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    qr_id: qr_id || "",
    mobile: "",
    name: "",
    password: "",
    address1: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    lat: "",
    long: "",
  });

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (qr_id) {
      dispatch(checkQRStatus(qr_id));
      setFormData((prev) => ({ ...prev, qr_id }));
    }
  }, [dispatch, qr_id]);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async ({ coords: { latitude, longitude } }) => {
        const addressData = await fetchAddressFromCoords(latitude, longitude);
        setFormData((prev) => ({
          ...prev,
          lat: String(latitude),
          long: String(longitude),
          city: addressData.city,
          state: addressData.state,
          pincode: addressData.pincode,
          address: addressData.fullAddress,
        }));
      },
      (err) => console.error("Geolocation error:", err)
    );
  }, []);

  useEffect(() => {
    if (redeemSuccess || redeemError) {
      setShowSplash(true);
      setTimeout(() => setShowText(true), 1000);
    }
  }, [redeemSuccess, redeemError]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const fetchAddressFromCoords = async (lat, long) => {
    const key = "AIzaSyBjwQjSQoYFe5OzJxbhnodl-usXAt6AH8g";
    const res = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${long}&key=${key}`
    );
    const data = await res.json();
    if (data.status !== "OK") throw new Error("Geocode failed");
    const comps = data.results[0].address_components;
    let city = "", state = "", pincode = "", district = "";
    comps.forEach(({ types, long_name }) => {
      if (types.includes("locality")) city = long_name;
      if (types.includes("administrative_area_level_1")) state = long_name;
      if (types.includes("postal_code")) pincode = long_name;
      if (types.includes("administrative_area_level_3")) district = long_name;
    });
    return {
      fullAddress: data.results[0].formatted_address,
      city, state, district, pincode,
    };
  };

  const buildPayload = (otpCode = "") => {
    const payload = {
      qr_id,
      mobile: formData.mobile,
      name: formData.name,
      password: formData.password,
      address1: formData.address1,
      address: formData.address,
      city: formData.city,
      state: formData.state,
      lat: String(formData.lat),
      long: String(formData.long),
      pincode: formData.pincode,
    };
    if (otpCode) payload.otp = otpCode;
    return payload;
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handlePlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();
    if (!place) return;
    let city = "", state = "", pincode = "", lat = "", long = "";
    place.address_components?.forEach(({ types, long_name }) => {
      if (types.includes("locality")) city = long_name;
      if (types.includes("administrative_area_level_1")) state = long_name;
      if (types.includes("postal_code")) pincode = long_name;
    });
    if (place.geometry?.location) {
      lat = String(place.geometry.location.lat());
      long = String(place.geometry.location.lng());
    }
    setFormData((prev) => ({
      ...prev,
      address: place.formatted_address,
      city, state, pincode, lat, long,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "mobile") {
      let cleaned = value.replace(/\D/g, "");
      if (cleaned.length === 1 && !/[6-9]/.test(cleaned[0])) return;
      cleaned = cleaned.slice(0, 10);
      setFormData((prev) => ({ ...prev, mobile: cleaned }));
      setMobileError(
        cleaned.length > 0 && cleaned.length < 10
          ? "Mobile number must be exactly 10 digits"
          : ""
      );
      if (autoFilled) setAutoFilled(false);
      if (skipOtp) setSkipOtp(false);
      // ✅ Reset login banner state on mobile change
      setAlreadyRegistered(false);
      setConsumerLoginUrl("");
      setOtpStep(false);
      clearTimeout(mobileDebounceRef.current);
      if (cleaned.length === 10) {
        mobileDebounceRef.current = setTimeout(async () => {
          const result = await dispatch(getConsumerDetails(cleaned));
          if (result?.success && result?.data) {
            const details = result.data?.data || result.data;
            // ✅ Auto-fill name but keep full form visible
            setFormData((prev) => ({
              ...prev,
              name: details.name || prev.name,
            }));
            setAutoFilled(true);
            setSkipOtp(true);
            setOtpStep(false);
            // ✅ Set login banner from response
            const isRegistered = result.data?.already_registered ?? false;
            setAlreadyRegistered(isRegistered);
            setConsumerLoginUrl(result.data?.login_url || "");
          }
        }, 400);
      }
      return;
    }

    if (name === "name") {
      const cleaned = value
        .replace(/[^a-zA-Z\s]/g, "")
        .replace(/^\s+/, "")
        .replace(/\s{2,}/g, " ");
      setFormData((prev) => ({ ...prev, [name]: cleaned }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (formData.mobile.length !== 10) {
      setMobileError("Mobile number must be exactly 10 digits");
      return;
    }
    if (!autoFilled && formData.password.length < 4) {
      alert("Please enter a 4-digit password.");
      return;
    }
    if (!formData.lat || !formData.long) {
      alert("Please select your location from the address field.");
      return;
    }
    if (skipOtp) {
      // ✅ Returning user — submit directly without OTP
      dispatch(redeemPromotionalQr(buildPayload()));
      return;
    }
    const result = await dispatch(sendOTP(qr_id, formData));
    if (result?.success) {
      setOtpStep(true);
      setOtpError("");
      const otpCode = result?.data?.otp_code?.toString();
      if (otpCode) {
        const digits = otpCode.slice(0, 4).split("");
        while (digits.length < 4) digits.push("");
        setOtp(digits);
        otpRefs.current[3]?.focus();
      }
    } else {
      setOtpError(result?.error || "Failed to send OTP");
    }
  };

  const handleVerifyOtp = async () => {
    const enteredOtp = otp.join("");
    if (enteredOtp.length < 4) {
      setOtpError("Please enter the 4-digit OTP");
      return;
    }
    dispatch(redeemPromotionalQr(buildPayload(enteredOtp)));
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);
    if (value && index < 3) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  // ── Loading screen ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-amber-50 flex flex-col items-center justify-center gap-3">
        <div className="w-10 h-10 border-4 border-amber-200 border-t-amber-600 rounded-full animate-spin" />
        <p className="text-amber-700 text-sm font-medium">Checking QR status…</p>
      </div>
    );
  }

  // ── Error screen ───────────────────────────────────────────────────────────
  if (error || statusInfo?.status !== 200) {
    return (
      <div className="min-h-screen bg-amber-400 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl border border-black shadow p-8 max-w-sm w-full text-center">
          <p className="text-red-600 font-medium text-md">
            {error?.message || statusInfo?.message || "QR code invalid or expired."}
          </p>
        </div>
      </div>
    );
  }

  // ── Splash / result screen ─────────────────────────────────────────────────
  if (showSplash) {
    const isSuccess = redeemSuccess;
    const splashAlreadyRegistered = redeemData?.already_registered;
    const loginUrl = redeemData?.login_url;
    const data = redeemData?.data;

    if (!isSuccess) {
      return (
        <div className="min-h-screen bg-amber-50 flex flex-col items-center justify-center text-center px-4 py-8">
          <DotLottieReact
            src="https://lottie.host/d807770f-57c5-4cee-b214-cbff9db0e282/pWayVMV0dn.lottie"
            loop
            autoplay
            style={{ width: 200, height: 200 }}
          />
          <h1 className="text-2xl font-bold text-red-600 mt-2">Redemption Failed</h1>
          <p className="text-sm text-gray-600 mt-2">
            {redeemData?.message || "Something went wrong. Please try again."}
          </p>
        </div>
      );
    }

    if (!splashAlreadyRegistered) {
      return (
        <div className="min-h-screen bg-amber-50 flex flex-col items-center justify-center text-center px-4 py-8">
          <DotLottieReact
            src="https://lottie.host/d807770f-57c5-4cee-b214-cbff9db0e282/pWayVMV0dn.lottie"
            loop
            autoplay
            style={{ width: 200, height: 200 }}
          />
          <h1 className="text-2xl font-bold text-amber-800 mt-2">
            {redeemData?.message || "QR Accumulated Successfully!"}
          </h1>
          <div className="bg-white rounded-2xl border border-amber-200 shadow-md w-full max-w-sm mt-6 overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-6 py-4">
              <p className="text-white text-xs font-semibold uppercase tracking-widest mb-1">Welcome</p>
              <p className="text-white text-xl font-bold">{data?.customer_name}</p>
            </div>
            <div className="px-6 py-4 space-y-3 text-left">
              <div className="flex justify-between text-sm">
                <span className="text-black font-medium">Status</span>
                <span className="inline-block bg-green-100 text-green-700 text-sm font-semibold px-3 py-1 rounded-full capitalize">
                  {data?.redemption_status}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 font-medium">Accumulated Points</span>
                <span className="inline-block bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1 rounded-full">
                  {data?.qr_points}
                </span>
              </div>
            </div>
            <div className="px-6 pb-6">
              <p className="text-sm text-black  text-center mb-3">
                Your account has been created. Login to track your rewards.
              </p>
              <a
                href={loginUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 rounded-xl transition shadow-sm hover:shadow-md text-sm text-center"
              >
                Click here to Login
              </a>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-amber-50 flex flex-col items-center justify-center text-center px-4 py-8">
        <DotLottieReact
          src="https://lottie.host/d807770f-57c5-4cee-b214-cbff9db0e282/pWayVMV0dn.lottie"
          loop
          autoplay
          style={{ width: 200, height: 200 }}
        />
        <h1 className="text-2xl font-bold text-amber-800 mt-2">
          {redeemData?.message || "QR Accumulated Successfully!"}
        </h1>
        <div className="bg-white rounded-2xl border border-amber-200 shadow-md w-full max-w-sm mt-6 overflow-hidden">
          <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-6 py-4">
            <p className="text-white text-xs font-semibold uppercase tracking-widest mb-1">Welcome Back</p>
            <p className="text-white text-xl font-bold">{data?.customer_name}</p>
          </div>
          <div className="px-6 py-4 space-y-3 text-left">
            <div className="flex justify-between text-sm">
              <span className="text-black font-medium">Status</span>
              <span className="inline-block bg-green-100 text-green-700 text-sm font-semibold px-3 py-1 rounded-full capitalize">
                {data?.redemption_status}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-black font-medium">QR Points</span>
              <span className="inline-block bg-amber-100 text-amber-700 text-sm font-bold px-3 py-1 rounded-full">
                {data?.qr_points}
              </span>
            </div>
          </div>
          {loginUrl && (
            <div className="px-6 pb-6">
              <p className="text-sm text-black text-center mb-3">
                Already an account exists. Please click here to login.
              </p>
              <a
                href={loginUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 rounded-xl transition shadow-sm hover:shadow-md text-sm text-center"
              >
                Click here to Login
              </a>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Main form card ─────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-amber-400 flex items-center justify-center px-4 py-2">
      <div className="bg-white rounded-2xl border border-amber-800 shadow-md w-full max-w-md p-8">

        {/* Header */}
        <div className="flex flex-col items-center mb-6">
          <h2 className="text-xl font-bold text-amber-800 text-center">
            {isPromotionalForm ? "Redeem Now" : isCashbackForm ? "Easy Rewardz" : "QR Redemption"}
          </h2>
          <span className="mt-3 inline-flex items-center gap-1.5 bg-amber-50 border border-amber-400 rounded-full px-3 py-1 text-sm font-medium text-amber-800">
            QR is Active & Valid
          </span>
        </div>

        {statusInfo?.message && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-amber-700 text-sm text-center font-medium mb-5">
            {statusInfo.message}
          </div>
        )}

        {showForm && (
          <form
            onSubmit={
              otpStep
                ? (e) => { e.preventDefault(); handleVerifyOtp(); }
                : handleSendOtp
            }
          >
            {/* Mobile */}
            <Field label="Mobile Number *">
              <div className="relative">
                <input
                  type="text"
                  name="mobile"
                  placeholder="10-digit mobile number"
                  value={formData.mobile}
                  onChange={handleChange}
                  required
                  className={inputClass}
                />
                {customerLoading && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2">
                    <svg className="w-4 h-4 animate-spin text-amber-500" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                  </span>
                )}
                {formData.mobile.length === 10 && !mobileError && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 text-base">✓</span>
                )}
              </div>
              {mobileError && (
                <p className="text-red-500 text-xs mt-1 ml-1">{mobileError}</p>
              )}
            </Field>

            {/* ✅ Auto-fill banner */}
            {autoFilled && (
              <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-3 py-2 mb-4 text-xs text-green-700 font-medium">
                <span className="text-green-500 text-base">✓</span>
                Details found & auto-filled from your previous redemption.
              </div>
            )}

            {/* Name */}
            <Field label="Name *">
              <input
                type="text"
                name="name"
                placeholder="Your full name"
                value={formData.name}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </Field>

            {/* ✅ Password hidden when customer details found (autoFilled) */}
            {/* {!autoFilled && (
              <Field label="4-Digit Password *">
                <input
                  type="password"
                  name="password"
                  placeholder="Enter 4-digit password"
                  value={formData.password}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 4);
                    setFormData((prev) => ({ ...prev, password: val }));
                  }}
                  maxLength={4}
                  inputMode="numeric"
                  required
                  className={inputClass}
                />
                {formData.password.length > 0 && formData.password.length < 4 && (
                  <p className="text-amber-600 text-xs mt-1 ml-1">
                    {4 - formData.password.length} more digit
                    {4 - formData.password.length !== 1 ? "s" : ""} needed
                  </p>
                )}
              </Field>
            )} */}

            {!autoFilled && (
              <Field label="4-Digit Password *">
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Enter 4-digit password"
                    value={formData.password}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "").slice(0, 4);
                      setFormData((prev) => ({ ...prev, password: val }));
                    }}
                    maxLength={4}
                    inputMode="numeric"
                    required
                    className={inputClass}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-amber-600 transition"
                    tabIndex={-1}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      // Eye-Off icon (hide)
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.477 0 8.268 2.943 9.542 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      // Eye icon (show)
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {formData.password.length > 0 && formData.password.length < 4 && (
                  <p className="text-amber-600 text-xs mt-1 ml-1">
                    {4 - formData.password.length} more digit
                    {4 - formData.password.length !== 1 ? "s" : ""} needed
                  </p>
                )}
              </Field>
            )}
            <div className="border-t border-amber-100 my-4" />

            {/* Place */}
            <Field label="Place *">
              <input
                type="text"
                name="address1"
                placeholder="Enter your place (e.g. Shop, Area, Landmark)"
                value={formData.address1}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </Field>

            {/* Address */}
            <Field label="Address *">
              <LoadScriptNext
                googleMapsApiKey="AIzaSyBjwQjSQoYFe5OzJxbhnodl-usXAt6AH8g"
                libraries={libraries}
              >
                <Autocomplete
                  onLoad={(ref) => (autocompleteRef.current = ref)}
                  onPlaceChanged={handlePlaceChanged}
                >
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Start typing your address…"
                    required
                    className={inputClass}
                  />
                </Autocomplete>
              </LoadScriptNext>
            </Field>

            {/* Button / OTP section */}
            {!otpStep ? (
              <button
                type="submit"
                disabled={otpLoading || (!autoFilled && formData.password.length < 4)}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 rounded-xl transition shadow-sm hover:shadow-md text-sm mt-2 disabled:opacity-60"
              >
                {otpLoading ? "Submitting…" : skipOtp ? "Redeem →" : "Send OTP →"}
              </button>
            ) : (
              <div className="mt-2">
                <p className="text-xs text-gray-500 text-center mb-3">
                  OTP sent to{" "}
                  <span className="font-semibold text-amber-600">{formData.mobile}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setOtpStep(false);
                      setOtp(["", "", "", ""]);
                      setOtpError("");
                    }}
                    className="ml-2 text-amber-500 underline text-xs"
                  >
                    Change
                  </button>
                </p>
                <div className="flex justify-center gap-2 mb-3">
                  {otp.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => (otpRefs.current[i] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(i, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(i, e)}
                      className="w-10 h-12 text-center text-lg font-bold border-2 border-gray-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-100 rounded-xl outline-none transition"
                    />
                  ))}
                </div>
                {(otpError || otpApiError) && (
                  <p className="text-red-500 text-xs text-center mb-2">
                    {otpError || otpApiError}
                  </p>
                )}
                <button
                  type="button"
                  onClick={handleVerifyOtp}
                  disabled={verifyLoading}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 rounded-xl transition shadow-sm hover:shadow-md text-sm disabled:opacity-60"
                >
                  {verifyLoading ? "Redeeming…" : "Verify & Redeem →"}
                </button>
                <p className="text-center text-xs text-gray-400 mt-3">
                  Didn't receive?{" "}
                  <button
                    type="button"
                    onClick={() => {
                      setOtp(["", "", "", ""]);
                      setOtpError("");
                    }}
                    className="text-amber-600 underline"
                  >
                    Resend OTP
                  </button>
                </p>
              </div>
            )}
          </form>
        )}

        {!showForm && (
          <p className="text-center text-gray-500 text-sm py-4">
            This QR is not valid for redemption.
          </p>
        )}
      </div>
    </div>
  );
};

export default PromotionalForm;